import { useSearchParams, type SetURLSearchParams } from "react-router";
import { type SortingState, type Updater } from "@tanstack/react-table";
import { type DocumentSummary } from "~/api/types";
import { useDocuments } from "~/api/v2/useDocuments";
import { filterDocuments } from "~/helpers/documentFilters";
import { useDocumentFilters } from "~/hooks/useDocumentFilters";
import { includesSearch } from "~/helpers/string";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { featureFlags } from "~/config/featureFlags";
import { useUserContext } from "~/hooks/useUserContext";
import { PlatformRole } from "~/config/types";
import { QueryParams } from "~/api/v2/useDocumentReleases";

const sortParam = "sort";

const defaultSorting: SortingState = [{ id: "title", desc: false }];

const parseSortingFromParams = (
  searchParams: URLSearchParams,
): SortingState => {
  const sortValue = searchParams.get(sortParam);
  if (!sortValue) return defaultSorting;
  const [id, direction] = sortValue.split(":");
  if (!id || (direction !== "asc" && direction !== "desc"))
    return defaultSorting;
  return [{ id, desc: direction === "desc" }];
};

const updateSearchParams = (
  searchParams: URLSearchParams,
  setSearchParams: SetURLSearchParams,
  apply: (next: URLSearchParams) => void,
) => {
  const next = new URLSearchParams(searchParams);
  apply(next);
  setSearchParams(next);
};

export const useDocumentsListModel = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const titleSearch = searchParams.get(QueryParams.Title);
  const isRefNumEnabled = useFeatureFlagEnabled(featureFlags.referenceNumber);

  const { data, isLoading, mutate } = useDocuments({ includeArchived: true });
  const { user } = useUserContext();

  const documents = data ?? [];
  const rawFilters = useDocumentFilters({
    documents,
    searchParams,
    setSearchParams,
  });

  const filters = {
    ...rawFilters,
    multiSelectFilterKeys:
      user.role === PlatformRole.reader
        ? rawFilters.multiSelectFilterKeys.filter(
            (key) => key !== "archiveState",
          )
        : rawFilters.multiSelectFilterKeys,
  };
  const sorting = parseSortingFromParams(searchParams);

  const filteredDocuments: DocumentSummary[] = filterDocuments(
    documents,
    filters.activeFilters,
  ).filter(
    (doc) =>
      !titleSearch ||
      includesSearch(doc.latestRelease.artifact.title, titleSearch) ||
      (isRefNumEnabled &&
        includesSearch(doc.referenceNumber ?? "", titleSearch)),
  );

  const handleSortingChange = (updater: Updater<SortingState>) => {
    const nextSorting =
      typeof updater === "function" ? updater(sorting) : updater;

    updateSearchParams(searchParams, setSearchParams, (next) => {
      const firstSort = nextSorting[0];
      if (firstSort) {
        next.set(
          sortParam,
          `${firstSort.id}:${firstSort.desc ? "desc" : "asc"}`,
        );
      } else {
        next.delete(sortParam);
      }
    });
  };

  const handleTitleSearchChange = (value: string) => {
    updateSearchParams(searchParams, setSearchParams, (next) => {
      if (value) {
        next.set(QueryParams.Title, value);
      } else {
        next.delete(QueryParams.Title);
      }
    });
  };

  return {
    documents,
    filteredDocuments,
    filters,
    hasData: data !== undefined,
    isLoading,
    mutate,
    sorting,
    titleSearch: titleSearch ?? "",
    handleSortingChange,
    handleTitleSearchChange,
  };
};

export type DocumentsListModel = ReturnType<typeof useDocumentsListModel>;
